import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import Avatar from "@material-ui/core/Avatar";
import Fab from "@material-ui/core/Fab";
import Chip from "@material-ui/core/Chip";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import PersonIcon from "@material-ui/icons/Person";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import UserModal from "../../components/UserModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import UserStatusIcon from "../../components/UserModal/statusIcon";
import { AuthContext } from "../../context/Auth/AuthContext";
import ForbiddenPage from "../../components/ForbiddenPage";
import { getBackendUrl } from "../../config";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import usePlans from "../../hooks/usePlans";
import whatsappIcon from "../../assets/nopicture.png";
import { useHistory } from "react-router-dom";

const backendUrl = getBackendUrl();

const reducer = (state, action) => {
  if (action.type === "LOAD_USERS") {
    const users = action.payload;
    const newUsers = [];
    users.forEach((user) => {
      const idx = state.findIndex((u) => u.id === user.id);
      if (idx !== -1) state[idx] = user; else newUsers.push(user);
    });
    return [...state, ...newUsers];
  }
  if (action.type === "UPDATE_USERS") {
    const user = action.payload;
    const idx = state.findIndex((u) => u.id === user.id);
    if (idx !== -1) { state[idx] = user; return [...state]; }
    return [user, ...state];
  }
  if (action.type === "DELETE_USER") {
    const idx = state.findIndex((u) => u.id === action.payload);
    if (idx !== -1) state.splice(idx, 1);
    return [...state];
  }
  if (action.type === "RESET") return [];
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  mobileList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1),
  },
  card: {
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    padding: theme.spacing(1.5),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  cardAvatar: {
    width: 46,
    height: 46,
    flexShrink: 0,
    border: `2px solid ${theme.palette.divider}`,
  },
  cardBody: {
    flex: 1,
    minWidth: 0,
  },
  cardName: {
    fontWeight: 600,
    fontSize: "0.95rem",
    color: theme.palette.text.primary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  cardEmail: {
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  cardMeta: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    marginTop: 4,
  },
  profileChip: {
    height: 20,
    fontSize: "0.68rem",
    fontWeight: 600,
  },
  cardActions: {
    display: "flex",
    flexShrink: 0,
    flexDirection: "column",
    gap: 2,
  },
  statusDot: {
    display: "flex",
    alignItems: "center",
  },
  mobileHeader: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    width: "100%",
    padding: theme.spacing(0, 0, 1),
  },
  mobileHeaderRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  fab: {
    position: "fixed",
    bottom: theme.spacing(3),
    right: theme.spacing(3),
    zIndex: 10,
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(6, 2),
    color: theme.palette.text.secondary,
    gap: theme.spacing(1),
  },
}));

const Users = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [users, dispatch] = useReducer(reducer, []);
  const [showInternalChat, setShowInternalChat] = useState(false);

  const { user: loggedInUser, socket } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      if (!loggedInUser.companyId) return;
      try {
        const planConfigs = await getPlanCompany(undefined, loggedInUser.companyId);
        setShowInternalChat(planConfigs?.plan?.useInternalChat || false);
      } catch (err) {}
    }
    fetchData();
  }, [loggedInUser.companyId]);

  useEffect(() => { dispatch({ type: "RESET" }); setPageNumber(1); }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/users/", { params: { searchParam, pageNumber } });
        dispatch({ type: "LOAD_USERS", payload: data.users });
        setHasMore(data.hasMore);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = loggedInUser.companyId;
    const onEvent = (data) => {
      if (data.action === "update" || data.action === "create")
        dispatch({ type: "UPDATE_USERS", payload: data.user });
      if (data.action === "delete")
        dispatch({ type: "DELETE_USER", payload: +data.userId });
    };
    socket.on(`company-${companyId}-user`, onEvent);
    return () => socket.off(`company-${companyId}-user`, onEvent);
  }, [socket, loggedInUser]);

  const handleOpenUserModal = () => { setSelectedUser(null); setUserModalOpen(true); };
  const handleCloseUserModal = () => { setSelectedUser(null); setUserModalOpen(false); };
  const handleSearch = (e) => setSearchParam(e.target.value.toLowerCase());
  const handleEditUser = (user) => { setSelectedUser(user); setUserModalOpen(true); };

  const handleDeleteUser = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      toast.success(i18n.t("users.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingUser(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) setPageNumber((p) => p + 1);
  };

  const handleCreateChat = async (targetUser) => {
    try {
      const { data } = await api.post("/chats", {
        users: [{ id: loggedInUser.id }, { id: targetUser.id }],
        isGroup: false,
        title: targetUser.name,
      });
      history.push(`/chats/${data.uuid}`);
    } catch (err) {
      toastError(err);
    }
  };

  const getAvatarUrl = (user) => {
    if (user.id === loggedInUser.id) {
      const saved = localStorage.getItem("profileImage");
      const img = saved || user.profileImage;
      return img
        ? `${backendUrl}/public/company${user.companyId}/user/${img}`
        : whatsappIcon;
    }
    return user.profileImage
      ? `${backendUrl}/public/company${user.companyId}/user/${user.profileImage}`
      : whatsappIcon;
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingUser && `${i18n.t("users.confirmationModal.deleteTitle")} ${deletingUser.name}?`}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteUser(deletingUser.id)}
      >
        {i18n.t("users.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <UserModal
        open={userModalOpen}
        onClose={handleCloseUserModal}
        aria-labelledby="form-dialog-title"
        userId={selectedUser && selectedUser.id}
      />

      {loggedInUser.profile === "user" ? <ForbiddenPage /> : (
        <>
          {isMobile ? (
            <>
              <MainHeader>
                <div className={classes.mobileHeader}>
                  <div className={classes.mobileHeaderRow}>
                    <Title>{i18n.t("users.title")}</Title>
                    <Typography variant="caption" color="textSecondary">
                      {users.length} usuarios
                    </Typography>
                  </div>
                  <TextField
                    fullWidth size="small" variant="outlined"
                    placeholder={i18n.t("contacts.searchPlaceholder")}
                    type="search" value={searchParam} onChange={handleSearch}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon style={{ color: "gray" }} fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
              </MainHeader>

              <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
                {users.length === 0 && !loading ? (
                  <div className={classes.emptyState}>
                    <PersonIcon style={{ fontSize: 48, opacity: 0.3 }} />
                    <Typography variant="body2">Sin usuarios</Typography>
                  </div>
                ) : (
                  <div className={classes.mobileList}>
                    {users.map((user) => (
                      <div key={user.id} className={classes.card}>
                        <Avatar
                          src={getAvatarUrl(user)}
                          alt={user.name}
                          className={classes.cardAvatar}
                          onError={(e) => { e.target.src = whatsappIcon; }}
                        />
                        <div className={classes.cardBody}>
                          <div className={classes.cardName}>{user.name}</div>
                          <div className={classes.cardEmail}>{user.email}</div>
                          <div className={classes.cardMeta}>
                            <div className={classes.statusDot}>
                              <UserStatusIcon user={user} />
                            </div>
                            <Chip
                              label={user.profile}
                              size="small"
                              className={classes.profileChip}
                              color={user.profile === "admin" ? "primary" : "default"}
                            />
                            {user.startWork && (
                              <Typography variant="caption" color="textSecondary">
                                {user.startWork}–{user.endWork}
                              </Typography>
                            )}
                          </div>
                        </div>
                        <div className={classes.cardActions}>
                          {showInternalChat && (
                            <IconButton size="small" onClick={() => handleCreateChat(user)}>
                              <ChatBubbleOutlineIcon fontSize="small" />
                            </IconButton>
                          )}
                          <IconButton size="small" onClick={() => handleEditUser(user)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => { setConfirmModalOpen(true); setDeletingUser(user); }}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </div>
                      </div>
                    ))}
                    {loading && (
                      <Typography variant="caption" align="center" color="textSecondary" style={{ textAlign: "center" }}>
                        Cargando...
                      </Typography>
                    )}
                  </div>
                )}
              </Paper>

              <Fab color="primary" className={classes.fab} onClick={handleOpenUserModal}>
                <AddIcon />
              </Fab>
            </>
          ) : (
            <>
              <MainHeader>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: 16 }}>
                  <Title>{i18n.t("users.title")} ({users.length})</Title>
                  <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                    <TextField
                      placeholder={i18n.t("contacts.searchPlaceholder")}
                      type="search" value={searchParam} onChange={handleSearch}
                      size="small" variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon style={{ color: "gray" }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Button variant="contained" color="primary" onClick={handleOpenUserModal}>
                      {i18n.t("users.buttons.add")}
                    </Button>
                  </div>
                </div>
              </MainHeader>

              <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">{i18n.t("users.table.ID")}</TableCell>
                      <TableCell align="center">{i18n.t("users.table.status")}</TableCell>
                      <TableCell align="center">Avatar</TableCell>
                      <TableCell align="center">{i18n.t("users.table.name")}</TableCell>
                      <TableCell align="center">{i18n.t("users.table.email")}</TableCell>
                      <TableCell align="center">{i18n.t("users.table.profile")}</TableCell>
                      <TableCell align="center">{i18n.t("users.table.startWork")}</TableCell>
                      <TableCell align="center">{i18n.t("users.table.endWork")}</TableCell>
                      <TableCell align="center">{i18n.t("users.table.actions")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell align="center">{user.id}</TableCell>
                        <TableCell align="center"><UserStatusIcon user={user} /></TableCell>
                        <TableCell align="center">
                          <Avatar
                            src={getAvatarUrl(user)}
                            alt={user.name}
                            style={{ width: 36, height: 36, margin: "0 auto" }}
                            onError={(e) => { e.target.src = whatsappIcon; }}
                          />
                        </TableCell>
                        <TableCell align="center">{user.name}</TableCell>
                        <TableCell align="center">{user.email}</TableCell>
                        <TableCell align="center">{user.profile}</TableCell>
                        <TableCell align="center">{user.startWork}</TableCell>
                        <TableCell align="center">{user.endWork}</TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => handleCreateChat(user)} disabled={!showInternalChat}>
                            <ChatBubbleOutlineIcon />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleEditUser(user)}>
                            <EditIcon />
                          </IconButton>
                          <IconButton size="small" onClick={() => { setConfirmModalOpen(true); setDeletingUser(user); }}>
                            <DeleteOutlineIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {loading && <TableRowSkeleton columns={9} />}
                  </TableBody>
                </Table>
              </Paper>
            </>
          )}
        </>
      )}
    </MainContainer>
  );
};

export default Users;
